/**
 * Pure helpers for property definitions: names, grouping, how a value reads,
 * which conditions suit a type, and matching an import column to a property.
 *
 * No React here, so the filter panel, the routing filter editor, the import
 * mapping and the tests can share one reading of a type.
 */
import type { PropertyDef, PropertyObject, PropertyType } from '../../api/properties';
import type { Vocab } from '../../vocab';

export const PROPERTY_TYPES: PropertyType[] = [
  'text', 'long_text', 'number', 'currency', 'date', 'datetime', 'select', 'multi_select',
  'boolean', 'user', 'email', 'phone', 'url',
];

export const TYPE_LABEL: Record<PropertyType, string> = {
  text: 'Text',
  long_text: 'Long text',
  number: 'Number',
  currency: 'Money (£)',
  date: 'Date',
  datetime: 'Date and time',
  select: 'Pick one',
  multi_select: 'Pick several',
  boolean: 'Yes or no',
  user: 'Team member',
  email: 'Email address',
  phone: 'Phone number',
  url: 'Web address',
};

export const OBJECTS: PropertyObject[] = ['person', 'company', 'deal', 'submission'];

/** An object's name in this organisation's words, plural. */
export function objectWord(v: Vocab, o: PropertyObject): string {
  switch (o) {
    case 'person': return v.Many('person');
    case 'company': return v.Many('company');
    case 'deal': return v.Many('deal');
    case 'submission': return v.Many('lead');
  }
}

export function hasOptions(kind: PropertyType): boolean {
  return kind === 'select' || kind === 'multi_select';
}

/** The heading a property with no group sits under. */
export const NO_GROUP = 'Other';

/**
 * Definitions under their headings, in position order: a group appears where
 * its first property does, and properties with no group come last, under
 * "Other".
 */
export function groupDefs(defs: PropertyDef[]): { group: string; defs: PropertyDef[] }[] {
  const out: { group: string; defs: PropertyDef[] }[] = [];
  const sorted = [...defs].sort((a, b) => a.position - b.position);
  for (const d of sorted) {
    const g = d.group_name.trim();
    if (!g) continue;
    const found = out.find((x) => x.group === g);
    if (found) found.defs.push(d);
    else out.push({ group: g, defs: [d] });
  }
  const loose = sorted.filter((d) => !d.group_name.trim());
  if (loose.length) out.push({ group: out.length ? NO_GROUP : '', defs: loose });
  return out;
}

/** Nothing to show: absent, null, an empty string or an empty list. */
export function isEmptyValue(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    || (Array.isArray(v) && v.length === 0);
}

const number = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 });
const pounds = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const dayTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function optionLabel(def: PropertyDef, value: string): string {
  return def.options.find((o) => o.value === value)?.label || value;
}

/**
 * A value as it reads on a page. Money is whole pence, as deal amounts are;
 * a lead's own currency values arrive typed into pence too.
 */
export function formatValue(def: PropertyDef | { type: PropertyType; options: PropertyDef['options'] }, value: unknown, names?: Record<string, string>): string {
  if (isEmptyValue(value)) return '';
  const d = def as PropertyDef;
  switch (def.type) {
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? number.format(n) : String(value);
    }
    case 'currency': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? pounds.format(n / 100) : String(value);
    }
    case 'date': {
      const t = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      return Number.isNaN(t.getTime()) ? String(value) : day.format(t);
    }
    case 'datetime': {
      const t = new Date(String(value).replace(' ', 'T'));
      return Number.isNaN(t.getTime()) ? String(value) : dayTime.format(t);
    }
    case 'boolean':
      if (value === true || value === 'true') return 'Yes';
      if (value === false || value === 'false') return 'No';
      return String(value);
    case 'select':
      return optionLabel(d, String(value));
    case 'multi_select':
      return (Array.isArray(value) ? value : String(value).split(','))
        .map((x) => optionLabel(d, String(x).trim()))
        .join(', ');
    case 'user':
      return names?.[String(value)] ?? String(value);
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}

/**
 * The conditions that make sense for a type, from those the engine serves
 * for the `field` filter. Text keeps them all. Dates are stored as text, so
 * "on a day" is equals and "in a month" starts with `2026-10`; greater and
 * less than compare numbers only, so they are offered for numbers alone.
 */
export function operatorsFor(kind: PropertyType | null | undefined, served: string[]): string[] {
  const empty = ['is_empty', 'is_not_empty'];
  let wanted: string[];
  switch (kind) {
    case 'number':
    case 'currency':
      wanted = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal', ...empty];
      break;
    case 'date':
    case 'datetime':
      wanted = ['equals', 'not_equals', 'starts_with', ...empty];
      break;
    case 'boolean':
      wanted = ['equals', 'not_equals', ...empty];
      break;
    case 'select':
      wanted = ['equals', 'not_equals', 'in', 'not_in', ...empty];
      break;
    case 'multi_select':
      wanted = ['contains', 'not_contains', ...empty];
      break;
    default:
      return served;
  }
  return served.filter((s) => wanted.includes(s));
}

/** How a condition reads for a type, where the served label says it badly. */
export function operatorLabel(kind: PropertyType | null | undefined, op: string, served: string): string {
  if (kind === 'date' || kind === 'datetime') {
    if (op === 'equals') return 'Is on';
    if (op === 'not_equals') return 'Is not on';
    if (op === 'starts_with') return 'In month (YYYY-MM)';
  }
  if (kind === 'select' && op === 'in') return 'Is any of';
  if (kind === 'select' && op === 'not_in') return 'Is none of';
  if (kind === 'multi_select' && op === 'contains') return 'Includes';
  if (kind === 'multi_select' && op === 'not_contains') return 'Does not include';
  return served;
}

/** A name compared loosely: case, spaces, dashes and underscores ignored. */
export function looseName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * The lead property an import column is, when its name is exactly the
 * property's key or label (case, spaces and underscores aside). Exact only,
 * as the import's own rule is: a wrong guess writes wrong data.
 */
export function propertyForColumn(column: string, defs: PropertyDef[]): PropertyDef | null {
  const c = looseName(column);
  if (!c) return null;
  return defs.find((d) => looseName(d.key) === c) ?? defs.find((d) => looseName(d.label) === c) ?? null;
}

/**
 * The value a record page sends for one property: whole pence for money,
 * `YYYY-MM-DD` for a date, a list for multi-select, and null to clear.
 */
export function cleanInput(kind: PropertyType, raw: unknown): unknown {
  if (isEmptyValue(raw)) return null;
  switch (kind) {
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
      return Number.isFinite(n) ? n : raw;
    }
    case 'currency': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[£,\s]/g, ''));
      return Number.isFinite(n) ? Math.round(n * 100) : raw;
    }
    default:
      return raw;
  }
}

/** Two stored values the same? Lists compare in order, everything else as text. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}
